/**
 * Slim app topbar that sits inside the main content column next to the
 * sidebar. Holds the mobile menu trigger, page title / breadcrumb, broker-
 * agent switcher (when applicable), theme toggle, notifications, and the
 * user menu.
 *
 * Mobile (< md): hamburger opens the sidebar in a Sheet drawer; logo
 * shown center-left (the fixed sidebar is hidden at this breakpoint).
 *
 * @changeHistory
 * - 2026-10-01: Exposed broker/admin workspace switching in the mobile drawer (Codex)
 * - 2026-05-09: Initial creation - slim topbar paired with AppSidebar (Claude)
 * - 2026-07-07: Switched breadcrumbs to shared route metadata with friendly dynamic labels (Codex)
 */

import { useQueries, type DataTag, type UndefinedInitialDataOptions } from '@tanstack/react-query';
import { Bell, ChevronRight, Menu } from 'lucide-react';
import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { getCDADetailsQueryOptions, type CDADetailsResponse } from '../../services/approval-api';
import { getBlogPostBySlugQueryOptions, type BlogPostRecord } from '../../services/blog-posts-api';
import {
  getAgentDetailsQueryOptions,
  getPlanQueryOptions,
  type AgentDetailRecord,
  type ApiError,
  type CompensationPlanRecord,
} from '../../services/api-client';
import {
  getCommercialListingQueryOptions,
  getListingDetailsQueryOptions,
  getResidentialListingQueryOptions,
  type ListingRecord,
} from '../../services/listings-api';
import { cn } from '../lib/utils';
import { getBreadcrumbDescriptors, type ResolvedBreadcrumb } from '../utils/route-metadata';
import AppSidebar, { type NavItem } from './AppSidebar';
import { ModeToggle } from './ModeToggle';
import { Button } from './ui/button';

import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

interface TopbarProps {
  navigation: NavItem[];
  switcher?: ReactNode;
  onLogout?: () => void;
}

type DynamicBreadcrumb = Extract<ResolvedBreadcrumb, { kind: 'dynamic' }>;

type AgentBreadcrumbQueryOptions = UndefinedInitialDataOptions<
  AgentDetailRecord,
  ApiError,
  string,
  readonly ['agent-details', string]
> & {
  queryKey: DataTag<readonly ['agent-details', string], AgentDetailRecord, ApiError>;
};

type ListingDetailBreadcrumbQueryOptions = UndefinedInitialDataOptions<
  ListingRecord,
  ApiError,
  string,
  readonly ['listing-details', string]
> & {
  queryKey: DataTag<readonly ['listing-details', string], ListingRecord, ApiError>;
};

type ListingBreadcrumbQueryOptions = UndefinedInitialDataOptions<
  ListingRecord,
  ApiError,
  string,
  readonly ['listing', string]
> & {
  queryKey: DataTag<readonly ['listing', string], ListingRecord, ApiError>;
};

type CommercialListingBreadcrumbQueryOptions = UndefinedInitialDataOptions<
  ListingRecord,
  ApiError,
  string,
  readonly ['commercial-listing', string]
> & {
  queryKey: DataTag<readonly ['commercial-listing', string], ListingRecord, ApiError>;
};

type BlogPostBreadcrumbQueryOptions = UndefinedInitialDataOptions<
  BlogPostRecord,
  Error,
  string,
  readonly ['blog-post', string]
> & {
  queryKey: DataTag<readonly ['blog-post', string], BlogPostRecord, Error>;
};

type CDABreadcrumbQueryOptions = UndefinedInitialDataOptions<
  CDADetailsResponse,
  Error,
  string,
  readonly ['cda', string]
> & {
  queryKey: DataTag<readonly ['cda', string], CDADetailsResponse, Error>;
};

type PlanBreadcrumbQueryOptions = UndefinedInitialDataOptions<
  CompensationPlanRecord,
  ApiError,
  string,
  readonly ['plan', number]
> & {
  queryKey: DataTag<readonly ['plan', number], CompensationPlanRecord, ApiError>;
};

type BreadcrumbLabelQueryOptions =
  | AgentBreadcrumbQueryOptions
  | ListingDetailBreadcrumbQueryOptions
  | ListingBreadcrumbQueryOptions
  | CommercialListingBreadcrumbQueryOptions
  | BlogPostBreadcrumbQueryOptions
  | CDABreadcrumbQueryOptions
  | PlanBreadcrumbQueryOptions;

const normalizeLabel = (value: unknown, fallbackLabel: string): string => {
  if (typeof value !== 'string') {
    return fallbackLabel;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallbackLabel;
};

const buildBreadcrumbLabelQuery = (crumb: DynamicBreadcrumb): BreadcrumbLabelQueryOptions => {
  switch (crumb.resolver) {
    case 'agent':
      return {
        ...getAgentDetailsQueryOptions(crumb.value),
        enabled: crumb.value.length > 0,
        select: (data: { agent: { fullName: string } }) =>
          normalizeLabel(data.agent.fullName, crumb.fallbackLabel),
      };
    case 'listing-detail':
      return {
        ...getListingDetailsQueryOptions(crumb.value),
        enabled: crumb.value.length > 0,
        select: (data: { address?: string }) => normalizeLabel(data.address, crumb.fallbackLabel),
      };
    case 'listing':
      return {
        ...getResidentialListingQueryOptions(crumb.value),
        enabled: crumb.value.length > 0,
        select: (data: { address?: string }) => normalizeLabel(data.address, crumb.fallbackLabel),
      };
    case 'commercial-listing':
      return {
        ...getCommercialListingQueryOptions(crumb.value),
        enabled: crumb.value.length > 0,
        select: (data: { address?: string }) => normalizeLabel(data.address, crumb.fallbackLabel),
      };
    case 'blog-post':
      return {
        ...getBlogPostBySlugQueryOptions(crumb.value),
        enabled: crumb.value.length > 0,
        select: (data: { title?: string }) => normalizeLabel(data.title, crumb.fallbackLabel),
      };
    case 'cda':
      return {
        ...getCDADetailsQueryOptions(crumb.value),
        enabled: crumb.value.length > 0,
        select: (data: { data: { cda: { property_address?: string } } }) =>
          normalizeLabel(data.data.cda.property_address, crumb.fallbackLabel),
      };
    case 'plan': {
      const planId = Number(crumb.value);

      return {
        ...getPlanQueryOptions(planId),
        enabled: Number.isFinite(planId) && planId > 0,
        select: (data: { planName?: string }) => normalizeLabel(data.planName, crumb.fallbackLabel),
      };
    }
    default:
      throw new Error(`Unsupported breadcrumb resolver: ${crumb.resolver}`);
  }
};

// const initials = (firstName?: string, lastName?: string): string =>
//   `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || 'U';

const Topbar = ({ navigation, switcher, onLogout }: TopbarProps): JSX.Element => {
  const location = useLocation();
  // const { user, logout } = useAuthStore();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const breadcrumbDescriptors = useMemo(
    () => getBreadcrumbDescriptors(location.pathname),
    [location.pathname]
  );
  const dynamicBreadcrumbs = useMemo(
    () =>
      breadcrumbDescriptors.filter((crumb): crumb is DynamicBreadcrumb => crumb.kind === 'dynamic'),
    [breadcrumbDescriptors]
  );
  const dynamicBreadcrumbResults = useQueries({
    queries: dynamicBreadcrumbs.map((crumb) => buildBreadcrumbLabelQuery(crumb)),
  });
  const breadcrumb = useMemo(() => {
    let dynamicIndex = 0;

    return breadcrumbDescriptors.map((crumb) => {
      if (crumb.kind === 'static') {
        return { label: crumb.label, href: crumb.href };
      }

      const result = dynamicBreadcrumbResults[dynamicIndex];
      dynamicIndex += 1;

      return {
        label: typeof result?.data === 'string' ? result.data : crumb.fallbackLabel,
        href: crumb.href,
      };
    });
  }, [breadcrumbDescriptors, dynamicBreadcrumbResults]);
  const mobileTitle = breadcrumb[breadcrumb.length - 1]?.label ?? 'Dashboard';

  // const handleLogout = onLogout ?? logout;

  return (
    <header
      className={cn(
        'sticky top-0 z-30 flex h-[6.25rem] items-center gap-2 border-b border-slate-200 bg-white/90 px-3 backdrop-blur supports-[backdrop-filter]:bg-white/75 md:h-[6.25rem] md:gap-4 md:px-6 dark:border-slate-800 dark:bg-slate-900/90 dark:supports-[backdrop-filter]:bg-slate-900/75'
      )}
    >
      {/* Mobile menu trigger + small logo */}
      <Sheet open={isMobileNavOpen} onOpenChange={setIsMobileNavOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-[280px] border-r border-slate-200 bg-white p-0 dark:border-slate-800 dark:bg-slate-900"
        >
          {switcher && <div className="px-4 pt-6 sm:hidden">{switcher}</div>}
          {/* Render the sidebar inside the drawer in expanded mode */}
          <AppSidebar
            navigation={navigation}
            variant="drawer"
            onNavigate={() => setIsMobileNavOpen(false)}
            onLogout={onLogout}
          />
        </SheetContent>
      </Sheet>

      {/* Mobile-only compact logo so the header doesn't look empty before
          the hamburger is opened. */}
      <Link to="/dashboard" className="flex items-center gap-2 md:hidden">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-600 text-xs font-bold text-white dark:bg-blue-500">
          CT
        </span>
      </Link>

      {/* Breadcrumb (desktop) */}
      <nav
        aria-label="Breadcrumb"
        className="hidden min-w-0 flex-1 items-center gap-1.5 text-sm text-slate-500 md:flex dark:text-slate-400"
      >
        {breadcrumb.map((crumb, index) => {
          const isLast = index === breadcrumb.length - 1;
          const crumbKey = crumb.href ?? `${crumb.label}-leaf`;
          return (
            <span key={crumbKey} className="flex items-center gap-1.5">
              {index > 0 && (
                <ChevronRight
                  className="h-3.5 w-3.5 text-slate-300 dark:text-slate-600"
                  aria-hidden="true"
                />
              )}
              {crumb.href && !isLast ? (
                <Link
                  to={crumb.href}
                  className="rounded px-1 py-0.5 text-sm font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className={cn(
                    'truncate px-1',
                    isLast
                      ? 'font-semibold text-slate-900 dark:text-slate-100'
                      : 'font-medium text-slate-500 dark:text-slate-400'
                  )}
                >
                  {crumb.label}
                </span>
              )}
            </span>
          );
        })}
      </nav>

      {/* Mobile page title fills the gap between hamburger and right-side actions */}
      <div className="flex min-w-0 flex-1 items-center md:hidden">
        <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
          {mobileTitle}
        </p>
      </div>

      <div className="flex items-center gap-1 md:gap-2">
        {switcher && <div className="hidden sm:block">{switcher}</div>}

        <ModeToggle />

        <Button
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          className="rounded-full text-slate-500 hover:bg-slate-100 hover:text-blue-600 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-blue-300"
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
        </Button>
      </div>
    </header>
  );
};

export default Topbar;
